/**
 * The product tour: which controls it points at, and whether it has run.
 *
 * ## Selectors, not refs
 *
 * A tour has to reach a dozen controls owned by a dozen components, most of
 * them several levels down and two of them inside `@qufolio/screener-grid`,
 * which may not import from this app. Threading a ref out of each one would put
 * a tour-shaped hole in every component's props for a feature that runs once.
 *
 * So each step names a CSS selector, and the rule is that **the selector must
 * be something the app already guarantees**. Three kinds qualify:
 *
 * - an `aria-label` or `title` that is a fixed literal — `Bars visible`,
 *   `Indicators`, `Choose columns`. An interpolated one does
 *   not: `Edit layouts, 2 charts` changes with the chart count, and
 *   `Chart type: Candlestick` with the setting.
 * - a class the app itself owns — `.qf-screener-island`, `.qf-track-cell`.
 * - a `data-tour` attribute added for this, where nothing else was stable.
 *
 * `data-tour` is the last resort, and every one is listed in `ADDED_ANCHORS`
 * below so the next person can find them all. No count here: this sentence has
 * already drifted once, and `tour.test.ts` now derives the set from the steps
 * and compares it, which prose cannot do.
 *
 * ## The first match wins, deliberately
 *
 * The chart controls are rendered once per chart, so a two-chart layout has two
 * `input[aria-label="Bars visible"]` and nothing in the DOM says which chart
 * either belongs to — `SectionGrid` takes `sectionId` as a prop and never
 * renders it. Pointing at the first is right for the case the tour exists for:
 * a new account opens on a one-chart layout. A tour that had to name a
 * particular chart would need an attribute on every cell, which is a change to
 * the grid for a walkthrough's benefit.
 *
 * ## A step whose target is missing is skipped, not shown
 *
 * The screener can be hidden, the star column is off without the track-list
 * capability, and a brand can switch features off entirely. Every one of those
 * makes a step's target absent, and a spotlight on nothing — or worse, on the
 * top-left corner, which is where a zero-size rect lands — reads as a broken
 * app. `resolve()` is what every caller goes through.
 */

import type { GuideSection } from "./guide";

/** One stop on the tour. */
export interface TourStep {
  /** Stable id — also what a resumed tour is addressed by. */
  id: string;
  /** What the spotlight lands on. The first match in the document wins. */
  selector: string;
  /** The card's heading. Short: this is a signpost, not the guide. */
  title: string;
  /** One or two sentences. Say what it is for, not what it is called. */
  body: string;
  /** The guide section "Read more" opens, when there is one worth opening. */
  section?: GuideSection;
  /**
   * More controls this step is about, folded into one spotlight.
   *
   * The ring is the union of every rect, so a step covering two adjacent
   * controls — how much history, and jump to a date — lights both rather than
   * describing one and pointing at the other.
   */
  also?: readonly string[];
  /**
   * A surface this step **opens**, named by its own selector.
   *
   * Some steps are about what is behind a button rather than the button. The
   * tour clicks the step's anchor, waits for this to appear, and spotlights it
   * instead — so the reader sees the panel being described. Leaving the step
   * closes it again.
   *
   * **Openness is read from this surface, not from the anchor.** Clicking
   * blindly would close a panel the reader had opened themselves, and tidying
   * up blindly would re-open one a stray click had dismissed — so the tour asks
   * whether the surface is on screen. The anchor's own `aria-pressed` will not
   * do: the strip's keys use it for *applied* rather than *shown*, and the
   * conditions key is pressed whenever a condition is filtering the table.
   */
  reveal?: string;
}

/** An element for a bare selector, or null — `resolve`'s rule, without a step. */
export function resolveSelector(selector: string): HTMLElement | null {
  try {
    const el = document.querySelector(selector);
    if (!(el instanceof HTMLElement)) return null;
    const rect = el.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0 ? el : null;
  } catch {
    return null;
  }
}

/**
 * The `data-tour` attributes added to the app for this feature, and where.
 *
 * Kept as a list because they are invisible to a reader of the component —
 * an attribute with no styling and no behaviour is exactly the thing a later
 * tidy-up deletes. If you remove one, remove its step too.
 */
export const ADDED_ANCHORS = [
  "layout-tabs — components/LayoutBar.tsx, on the *current* tab button",
  "interval — components/IntervalPicker.tsx, its root row",
  "screens — components/ScreenerBar.tsx, the two screen buttons and ▾",
  "universe — components/ScreenerBar.tsx, the ◎ button (its text is the universe)",
  "universe-picker — components/ScreenerBar.tsx, via <Menu tourId>, the two-step picker",
  "group-chips — components/ScreenerBar.tsx, the chips' box",
  "status — components/LayoutBar.tsx, the feed and save cluster",
  "layouts — components/LayoutBar.tsx, the ✎ circle (its title and label carry the count)",
  "symbol-data — App.tsx, the wrapper round the data key (its label carries the symbol)",
  "data-panel — components/SymbolDataPanel.tsx, the panel's root",
  "indicator-picker — components/IndicatorPicker.tsx, its dialog (labelled by id, not a literal)",
  "conditions — App.tsx, the wrapper round <SavedFilterPicker>",
  "span-presets — components/ChartGroupView.tsx, the 1Y / YTD / ›| row",
] as const;

/**
 * The stops, in order.
 *
 * It follows the guide's own interface map, because somebody who takes the tour
 * and then opens the guide should find the same shape twice rather than two
 * tours of the same screen.
 */
export const TOUR_STEPS: readonly TourStep[] = [
  {
    id: "layout-tabs",
    selector: '[data-tour="layout-tabs"]',
    title: "Your Layouts",
    body: "Each tab is a layout — an arrangement of charts that keeps its own symbols and intervals. Switching between them is instant and costs you nothing.",
    section: "layouts",
  },
  {
    id: "layout-panel",
    // A `data-tour`, not the title: the title carries the chart count
    // (`Edit layouts · 2 charts`) since the circle became a pencil.
    selector: '[data-tour="layouts"]',
    // The panel is the subject, so the tour opens it. Its own `role="dialog"`
    // and `aria-label` are a fixed literal — no `data-tour` needed — and no
    // other dialog in the app is labelled "Layout".
    reveal: '[role="dialog"][aria-label="Layout"]',
    title: "Customize Layout",
    body: "This panel is behind the pencil circle beside your tabs. It holds the arrangements, which side the screener sits on, your saved templates, and what is linked across charts.",
    section: "layout-panel",
  },
  {
    // No `data-tour` needed: `role="combobox"` with `aria-haspopup="dialog"` is
    // the toolbar's box and nothing else. `SymbolDialog`'s own search input is
    // also a combobox but has no popup, so the pair is unique.
    id: "symbol",
    selector: 'input[role="combobox"][aria-haspopup="dialog"]',
    title: "Change the symbol",
    body: "Click it, or just start typing anywhere on the page — a letter opens the search. Enter charts what you picked.",
    section: "symbol",
  },
  {
    id: "interval",
    selector: '[data-tour="interval"]',
    // The presets sit at the foot of the chart, far from the box at the top —
    // two rings rather than one stretched between them.
    also: ['[data-tour="span-presets"]'],
    title: "Select Period - Multiplier and Interval",
    body: "Not a fixed menu: 1 D, 4 H, 15 MI are all just a number and a unit, so an interval nobody thought to list is still one you can type. Down on the chart, 1Y and YTD jump to a span, and ›| returns to the latest bar.",
    section: "interval",
  },
  {
    id: "visible-bars",
    selector: 'input[aria-label="Bars visible"]',
    // The two boxes sit side by side and answer the same question from
    // different ends, so one ring covers both.
    also: ['input[aria-label="Go to date"]'],
    title: "How much history, and where in it",
    body: "The first box is how many bars are on screen — it moves the window over data you already have, so it is instant and never refetches. The one beside it takes a date and centres the chart on it.",
    section: "history",
  },
  {
    id: "indicators",
    selector: 'button[aria-label="Indicators"]',
    reveal: '[data-tour="indicator-picker"]',
    title: "Add indicators to chart",
    body: "The picker searches names and descriptions both, so a column headed fM2 is findable by typing “momentum”. Some indicators go over the price, some open a pane of their own, and the list says which before you add it.",
    section: "indicators",
  },
  {
    // The screener bar's first part (`docs/plans/screener-bar.md`): the two
    // screen buttons and ▾, wrapped by one `data-tour` because each button's
    // own label is a screen's name.
    id: "screens",
    selector: '[data-tour="screens"]',
    title: "Two screens, one click apart",
    body: "The filled button is the screen on show; the one beside it is the screen you had before — click it to swap back. ▾ lists every screen, with New and Edit this screen at the foot.",
    section: "screener",
  },
  {
    // ◎'s own text is the universe in words, so it is found by `data-tour`.
    // The picker is the subject, so the tour opens it.
    id: "universe",
    selector: '[data-tour="universe"]',
    reveal: '[data-tour="universe-picker"]',
    title: "Group of symbols shown in table",
    body: "◎ is the universe the table screens. Choose a kind — Standard, Market Cap, Themes, Trades, or Lists of your own — then tick its groups. Tick several and they combine with OR or AND.",
    section: "screener-groups",
  },
  {
    id: "group-chips",
    selector: '[data-tour="group-chips"]',
    title: "Switch a group off without losing it",
    body: "Each group you picked is a chip. Click one to switch it off — it stays on the bar, struck through, and leaves the table and the count — and click again to bring it back. The ✕ takes it out.",
    section: "screener-groups",
  },
  {
    id: "screener",
    selector: ".qf-screener-island",
    title: "The screener",
    body: "One row per symbol, with whatever columns you choose, refreshing on its own. Clicking a row charts that symbol — that is the main way the two halves of the screen talk to each other.",
    section: "screener",
  },
  {
    id: "columns",
    selector: 'button[title="Choose columns"]',
    // ⋯ sits beside it at the end of the bar and holds what the bar has no
    // room for, so one ring covers both.
    also: ['button[aria-label="More screener tools"]'],
    title: "Choose columns",
    body: "A hundred or more columns, searchable by description as well as by name. Show all and hide all act on whatever the search is showing, which is what makes them safe to press. ⋯ beside it keeps the rest — markets, and hiding the screener.",
    section: "columns",
  },
  {
    id: "saved-conditions",
    selector: 'button[aria-label="Saved conditions"]',
    reveal: '[data-tour="conditions"]',
    title: "Conditions You Named",
    body: "A filter built in the editor and saved with a name. Tick several and they all apply together, so each one you tick makes the list shorter. Hovering this key reads the list; clicking keeps it open.",
    section: "saved-filters",
  },
  {
    id: "track-lists",
    selector: ".qf-track-cell",
    title: "Bookmark worth tracking",
    body: "The star files a symbol into a list of your own. Select several rows and a digit key files them all at once.",
    section: "track-lists",
  },
  {
    // The key's own label carries the symbol, so the anchor is the button
    // inside the wrapper that `data-tour` marks.
    id: "symbol-data",
    selector: '[data-tour="symbol-data"] button',
    reveal: '[data-tour="data-panel"]',
    title: "Everything else about a symbol",
    body: "This takes the screener's place and answers the questions a chart cannot: the profile, its peers, the news, and Fin St — the financial statements. Hover the key to peek at it; click to keep it. The same key puts the screener back.",
  },
  {
    id: "status",
    selector: '[data-tour="status"]',
    title: "There is no Save button",
    body: "Everything you change is written back a moment after you stop changing it. This corner is how you know — it says Saved when it is.",
    section: "saved",
  },
];

/**
 * Bumped when the tour changes enough that somebody who has seen it should see
 * it again. A plain integer rather than the app version: most releases do not
 * touch the tour, and re-running it on every deploy is how a walkthrough turns
 * into an irritation.
 */
export const TOUR_VERSION = 2; // 2: the screener bar (2026-10-16)

const SEEN_KEY = "qufolio.tour.seen";

/**
 * Below this, the tour does not run.
 *
 * The narrow-screen shell has no tool strip, no layout panel and no screener
 * beside the chart, so most steps would resolve to nothing and the tour would
 * be three cards long. `mobile.md` owns that shell; a tour of it is a separate
 * script, not this one with holes in it.
 */
export const MIN_WIDTH = 900;

/**
 * Has this browser seen this version of the tour?
 *
 * **localStorage, which means per-browser.** There is nowhere better today: the
 * workspace contract is `additionalProperties: false`, so it cannot carry the
 * flag without a schema change, and there is no per-user settings row on the
 * server at all. The cost is that somebody who signs in on a second machine is
 * offered the tour again, which is a far smaller problem than a migration in a
 * service that is off limits.
 *
 * Every access is wrapped: a private window or blocked site data throws here,
 * and a tour that crashes the app on load is worse than no tour.
 */
export function tourSeen(): boolean {
  try {
    return Number(window.localStorage.getItem(SEEN_KEY)) >= TOUR_VERSION;
  } catch {
    // Unreadable storage reads as "seen": the tour is a nicety, and showing it
    // on every single load to somebody whose browser cannot remember is the
    // worse failure of the two.
    return true;
  }
}

/** Remember that it ran — on finishing *or* skipping, since both are answers. */
export function markTourSeen(): void {
  try {
    window.localStorage.setItem(SEEN_KEY, String(TOUR_VERSION));
  } catch {
    // Nothing to do. The tour has already been shown; failing to record it
    // costs one repeat, and there is no user-facing action that would help.
  }
}

/** Forget it, so the next load offers it again. Used by the menu's replay. */
export function resetTourSeen(): void {
  try {
    window.localStorage.removeItem(SEEN_KEY);
  } catch {
    /* as above */
  }
}

/** The element a step points at, or null when it is not on screen. */
export function resolve(step: TourStep): HTMLElement | null {
  try {
    const el = document.querySelector(step.selector);
    if (!(el instanceof HTMLElement)) return null;
    // A control scrolled out of an overflow:hidden container, or one hidden by
    // `display:none`, measures zero. Spotlighting that puts the ring in the
    // top-left corner over nothing.
    const rect = el.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0 ? el : null;
  } catch {
    // An invalid selector is a typo in the step list, not a reason to take the
    // app down.
    return null;
  }
}

/** The steps whose targets are on screen right now. */
export function liveSteps(): TourStep[] {
  return TOUR_STEPS.filter((step) => resolve(step) !== null);
}

/**
 * The steps that are rendered by the app's chrome, before any data arrives.
 *
 * `<LayoutBar>` is outside the gate that waits for the workspace, so these
 * three resolve on the very first paint. **They must not be able to satisfy
 * the auto-start floor on their own**, which is the bug this set exists to
 * close: with a plain "two or more steps resolve" test, a cold backend meant
 * the tour opened 500ms in as a three-stop tour of the top row — no symbol, no
 * indicators, no screener — and because `steps` is fixed at open and finishing
 * records the flag, that was the *only* tour that person was ever offered.
 */
const CHROME_STEPS = new Set(["layout-tabs", "layout-panel", "status"]);

/**
 * The steps that prove *data* arrived, not merely that the grid mounted.
 *
 * The chrome floor alone is not enough, because the whole chart toolbar —
 * interval, bars visible, symbol — and the group bar render in the same commit
 * as the layout, with no data required. The screener's island exists only once
 * a frame has landed, and the star needs rows on top of that. So without this
 * the tour still opened a step or two before the two stops it most wants to
 * show, and they were dropped: the step list is fixed when the tour opens.
 */
const DATA_STEPS = new Set(["screener", "track-lists"]);

/**
 * Should the tour start on its own?
 *
 * Deliberately not "is this a new account" — the app does not know that, and
 * the question the tour answers is "has this person been shown around", which
 * is what the flag records.
 */
export function shouldAutoStart(relaxed = false): boolean {
  if (tourSeen()) return false;
  if (window.innerWidth < MIN_WIDTH) return false;
  const live = liveSteps();
  // **At least two stops that are not chrome.** Counting all ten would let the
  // top row alone clear the bar while the workspace was still loading, and the
  // tour only ever opens once.
  if (live.filter((step) => !CHROME_STEPS.has(step.id)).length < 2) return false;
  // **And normally at least one that needed data**, so the screener stops are
  // in the list rather than a second behind it.
  //
  // `relaxed` is the caller saying it has waited long enough. The two cases
  // are indistinguishable from the DOM — a screener that is *slow* and one that
  // is *switched off for this brand* both look like an absent island — so the
  // poll waits for data and then gives up on it, rather than choosing between
  // "never tour a brand without a screener" and "usually drop the screener
  // steps".
  if (!relaxed && !live.some((step) => DATA_STEPS.has(step.id))) return false;
  return true;
}
